import { Bath, Bed, ChevronLeft, ChevronRight, DoorOpen, ExternalLink, Home, MapPin, Ruler, Target, TrendingUp, X } from 'lucide-react';
import React from 'react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';
import { RealEstate } from '../../types/RealEstate';
import { useRealEstateSystem } from '../../hooks/useRealEstateSystem';

interface RealEstateDetailsProps {
  property: RealEstate;
  onClose: () => void;
}

export function RealEstateDetails({ property, onClose }: RealEstateDetailsProps) {
  const { colors } = useDesignColors();
  const { theme } = useThemeSettings();
  const { settings: multilingualSettings } = useMultilingualSettings();
  const { tags } = useRealEstateSystem();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const isDark = theme.mode === 'dark';

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get translations for the current language
  const currentLang = multilingualSettings.currentLanguage || 'fr';
  const translations = multilingualSettings.translations[currentLang];
  const propertyTranslation = translations?.miniApps?.find(app => app.id === 'realestate')?.content?.propertyDetails?.[property.id];

  // Get translated property data
  const translatedProperty = {
    ...property,
    title: propertyTranslation?.title || property.title,
    description: propertyTranslation?.description || property.description,
    features: propertyTranslation?.features || property.features
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Vérifier si les champs optionnels sont définis, non nuls et non vides
  const hasRoi = property.target_roi !== undefined && property.target_roi !== null && property.target_roi !== 0;
  const hasResale = property.estimated_resale !== undefined && property.estimated_resale !== null && property.estimated_resale !== '';
  const hasLocation = property.address || property.city || property.postal_code || property.country;
  const hasFeatures = property.features && property.features.length > 0;
  const hasExternalUrl = property.metadata?.external_url;
  const hasDescription = property.description && property.description.trim() !== '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className={`w-full ${isMobile ? 'h-full' : 'max-w-[1600px] h-[90vh]'} flex flex-col overflow-hidden rounded-xl border ${
        isDark ? 'bg-gray-900/95 border-white/10' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${
          isDark ? 'border-white/10' : 'border-gray-200'
        }`}>
          <h3 className="text-xl sm:text-2xl font-semibold text-white line-clamp-1">
            {translatedProperty.title}
          </h3>
          <button
            onClick={onClose}
            className={`text-gray-400 hover:text-${isDark ? 'white' : 'black'} transition-colors p-2`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-8">
            <div className={`flex flex-col ${isMobile ? 'gap-6' : 'lg:flex-row lg:gap-12'}`}>
              {/* Images */}
              <div className={`${isMobile ? 'w-full' : 'lg:w-2/3'} space-y-6`}>
                {property.images && property.images.length > 0 && (
                  <div className="relative">
                    <div className="aspect-[16/9] rounded-xl overflow-hidden bg-black/20">
                      <img
                        src={property.images[currentImageIndex]}
                        alt={translatedProperty.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {property.images && property.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(prev => (prev === 0 ? (property.images?.length || 1) - 1 : prev - 1))}
                          className="absolute left-4 top-1/2 -translate-y-1/2  p-2 sm:p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                        >
                          <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(prev => (prev === (property.images?.length || 1) - 1 ? 0 : prev + 1))}
                          className="absolute right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                        >
                          <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
                        </button>
                      </>
                    )}
                  </div>
                )}

                {property.images && property.images.length > 1 && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 sm:gap-4">
                    {property.images.map((image: string, index: number) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`aspect-square rounded-xl overflow-hidden ${
                          index === currentImageIndex ? 'ring-4 ring-indigo-500' : ''
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${translatedProperty.title} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Informations */}
              <div className={`${isMobile ? 'w-full' : 'lg:w-1/3'} space-y-6 sm:space-y-8`}>
                {/* Prix et statut */}
                <div className="flex items-center justify-between">
                  {property.price && (
                    <div 
                      className="text-2xl sm:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                      }}
                    >
                      {formatPrice(property.price, property.currency)}
                      {property.transaction_type === 'rental' && '/mois'}
                    </div>
                  )}
                  <div className={`px-4 py-2 rounded-full text-base font-medium ${
                    property.status === 'available' 
                      ? 'bg-green-500/20 text-green-400'
                      : property.status === 'sold'
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-orange-500/20 text-orange-400'
                  }`}>
                    {property.status === 'available' ? 'Disponible' : property.status === 'sold' ? 'Vendu' : 'Loué'}
                  </div>
                </div>

                {/* Caractéristiques principales */}
                <div className="grid grid-cols-2 gap-4 sm:gap-6">
                  <div className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <Home className="w-6 h-6" />
                    <span className="text-lg">{property.type}</span>
                  </div>
                  {property.surface && (
                    <div className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <Ruler className="w-6 h-6" />
                      <span className="text-lg">{property.surface} m²</span>
                    </div>
                  )}
                  {property.rooms && (
                    <div className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <DoorOpen className="w-6 h-6" />
                      <span className="text-lg">{property.rooms} pièces</span>
                    </div>
                  )}
                  {property.bedrooms && (
                    <div className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <Bed className="w-6 h-6" />
                      <span className="text-lg">{property.bedrooms} chambres</span>
                    </div>
                  )}
                  {property.bathrooms && (
                    <div className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <Bath className="w-6 h-6" />
                      <span className="text-lg">{property.bathrooms} SDB</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                {hasDescription && (
                  <div>
                    <h4 
                      className="text-lg font-medium mb-3 bg-clip-text text-transparent bg-gradient-to-r"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                      }}
                    >
                      Description
                    </h4>
                    <p className={`text-base leading-relaxed break-words whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {translatedProperty.description}
                    </p>
                  </div>
                )}

                {/* Localisation */}
                {hasLocation && (
                  <div>
                    <h4 
                      className="text-lg font-medium mb-3 bg-clip-text text-transparent bg-gradient-to-r"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                      }}
                    >
                      Localisation
                    </h4>
                    <div className={`flex items-start gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <MapPin className="w-6 h-6 shrink-0 mt-0.5" />
                      <div className="text-base">
                        {property.address && <div>{property.address}</div>}
                        {property.postal_code && property.city && (
                          <div>{property.postal_code} {property.city}</div>
                        )}
                        {property.country && <div>{property.country}</div>}
                      </div>
                    </div>
                  </div>
                )}

                {/* Rentabilité et revente */}
                {(hasRoi || hasResale) && (
                  <div className="grid grid-cols-2 gap-6">
                    {hasRoi && (
                      <div>
                        <h4 
                          className="text-lg font-medium mb-3 bg-clip-text text-transparent bg-gradient-to-r"
                          style={{
                            backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                          }}
                        >
                          Rentabilité cible
                        </h4>
                        <div className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Target className="w-6 h-6" />
                          <span className="text-lg">{property.target_roi}%</span>
                        </div>
                      </div>
                    )}
                    
                    {hasResale && (
                      <div>
                        <h4 
                          className="text-lg font-medium mb-3 bg-clip-text text-transparent bg-gradient-to-r"
                          style={{
                            backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                          }}
                        >
                          Revente estimée
                        </h4>
                        <div className={`flex items-center gap-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <TrendingUp className="w-6 h-6" />
                          <span className="text-lg">{property.estimated_resale}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Lien externe */}
                {hasExternalUrl && (
                  <div>
                    <h4 
                      className="text-lg font-medium mb-3 bg-clip-text text-transparent bg-gradient-to-r"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                      }}
                    >
                      Plus d'informations
                    </h4>
                    <a 
                      href={property.metadata?.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 px-6 py-3 rounded-xl text-base transition-colors"
                      style={{
                        background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                        color: colors.gradientStart
                      }}
                    >
                      <ExternalLink className="w-5 h-5" />
                      Voir la fiche complète
                    </a>
                  </div>
                )}

                {/* Tags */}
                {property.tags && property.tags.length > 0 && (
                  <div>
                    <h4 
                      className="text-lg font-medium mb-3 bg-clip-text text-transparent bg-gradient-to-r"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                      }}
                    >
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {property.tags.map(tagId => {
                        const tag = tags.find(t => t.id === tagId);
                        if (tag) {
                          return (
                            <span
                              key={tag.id}
                              className={`px-3 py-1.5 rounded-lg text-sm ${
                                isDark 
                                  ? 'bg-white/5 text-gray-300' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {tag.name}
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                )}

                {/* Caractéristiques */}
                {hasFeatures && (
                  <div>
                    <h4 
                      className="text-lg font-medium mb-3 bg-clip-text text-transparent bg-gradient-to-r"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                      }}
                    >
                      Caractéristiques
                    </h4>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {translatedProperty.features?.map((feature: string, index: number) => (
                        <span
                          key={index}
                          className={`px-3 py-2 rounded-xl text-base ${
                            isDark 
                              ? 'bg-white/5 text-gray-300' 
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}